import { UnblockDependentsConfig } from "../agent-config-options";

/**
 * Default for whether the unblock-dependents sweep runs at all. When
 * the consumer omits `UnblockDependentsConfig`, the bundle ships with
 * the sweep **enabled** so every `status:done` transition
 * automatically propagates into the dependency graph.
 *
 * @see UnblockDependentsConfig
 */
export const DEFAULT_UNBLOCK_DEPENDENTS_ENABLED = true;

/**
 * Default comment body posted on a newly-unblocked issue. Variables
 * are substituted at runtime by the `unblock-dependents.sh` script:
 *
 * - `<CLOSED_ISSUE>` — the `#<n>` reference to the issue whose
 *   closure triggered the sweep (e.g. `#123`).
 *
 * The placeholder syntax deliberately avoids `{{curly-brace}}` form —
 * `AgentConfig`'s template resolver claims that namespace at rule
 * generation time, so a `{{closedIssue}}` placeholder would be
 * rewritten before the agent ever saw it.
 */
export const DEFAULT_UNBLOCK_COMMENT_TEMPLATE =
  "Dependencies resolved by <CLOSED_ISSUE> — unblocking.";

/**
 * Default comment body posted on a dependent whose other dependencies
 * are still open (partial unblock). Variables substituted at runtime:
 *
 * - `<CLOSED_ISSUE>` — the just-closed issue (`#<n>`).
 * - `<OPEN_DEPS>` — space-separated list of remaining open
 *   dependencies (e.g. `#45 #47`).
 *
 * Applied when `flagPartialUnblockWithAttention` is `true`; otherwise
 * no comment is posted and the dependent is left untouched.
 */
export const DEFAULT_PARTIAL_UNBLOCK_COMMENT_TEMPLATE =
  "Dependency <CLOSED_ISSUE> resolved, but still waiting on: <OPEN_DEPS>.";

/**
 * Fully-resolved unblock-dependents settings. Every field is defaulted
 * so downstream renderers can reason about a single canonical shape.
 */
export interface ResolvedUnblockDependents {
  readonly enabled: boolean;
  readonly commentTemplate: string;
  readonly flagPartialUnblockWithAttention: boolean;
  readonly partialUnblockCommentTemplate: string;
}

/**
 * Resolve a (possibly absent) `UnblockDependentsConfig` into a canonical
 * `ResolvedUnblockDependents` with every field filled in. Unset fields
 * cascade from their documented defaults.
 *
 * Malformed configs (empty / whitespace-only `commentTemplate`) throw
 * a descriptive `Error` — callers should not need to guard against it
 * at runtime.
 */
export function resolveUnblockDependents(
  config?: UnblockDependentsConfig,
): ResolvedUnblockDependents {
  const commentTemplate =
    config?.commentTemplate ?? DEFAULT_UNBLOCK_COMMENT_TEMPLATE;
  assertNonEmpty(commentTemplate, "commentTemplate");

  const partialUnblockCommentTemplate =
    config?.partialUnblockCommentTemplate ??
    DEFAULT_PARTIAL_UNBLOCK_COMMENT_TEMPLATE;
  assertNonEmpty(
    partialUnblockCommentTemplate,
    "partialUnblockCommentTemplate",
  );

  return {
    enabled: config?.enabled ?? DEFAULT_UNBLOCK_DEPENDENTS_ENABLED,
    commentTemplate,
    flagPartialUnblockWithAttention:
      config?.flagPartialUnblockWithAttention ?? false,
    partialUnblockCommentTemplate,
  };
}

/**
 * Synth-time validation hook. Throws a descriptive `Error` when the
 * supplied `UnblockDependentsConfig` is malformed. Called by
 * `AgentConfig.preSynthesize` before any rendering so a misconfigured
 * sweep fails the build instead of silently shipping a broken
 * procedure script. Returns the resolved config unchanged so callers
 * can write `const ud = validateUnblockDependentsConfig(config)` in
 * one line.
 *
 * Malformed cases rejected here:
 *
 * - `commentTemplate` empty or whitespace-only.
 * - `partialUnblockCommentTemplate` empty or whitespace-only.
 */
export function validateUnblockDependentsConfig(
  config?: UnblockDependentsConfig,
): ResolvedUnblockDependents {
  return resolveUnblockDependents(config);
}

/**
 * Render the markdown subsection appended to the
 * `orchestrator-conventions` rule. Always returns a non-empty string
 * so the orchestrator rule documents the contract even when the
 * consumer relies on the defaults.
 */
export function renderUnblockDependentsSection(
  ud: ResolvedUnblockDependents,
): string {
  const lines: Array<string> = [
    "## Agent-driven unblocking",
    "",
    "After any agent applies `status:done` to an issue, it runs a",
    "**targeted unblock sweep** so downstream issues that were only",
    "waiting on that issue transition to `status:ready` immediately —",
    "without waiting for the next orchestrator dispatch cycle. Without",
    "this sweep, dependents could sit in `status:blocked` for minutes",
    "to hours (the scheduled-task cadence) after their last dependency",
    "closed.",
    "",
  ];

  if (!ud.enabled) {
    lines.push(
      "**Agent-driven unblocking is disabled for this project.** Agents",
      "do not run the targeted sweep after closing an issue; dependents",
      "wait for the next orchestrator dispatch (Phase C) to pick up the",
      "resolved dependency. Enable the sweep via",
      "`AgentConfigOptions.unblockDependents.enabled = true` once the",
      "pipeline tolerates automated label transitions.",
      "",
    );
    return lines.join("\n");
  }

  lines.push(
    "### When the sweep runs",
    "",
    "The merge-flow agents run the sweep as the last step of the",
    "`status:done` transition they own. Other bundles that close an",
    "issue directly must wire the sweep explicitly — there is no",
    "global hook, so a new closing path that skips these call sites",
    "silently defers unblocking to the next orchestrator dispatch.",
    "Current wired hook points:",
    "",
    "- The `pr-reviewer` sub-agent, Phase 5 (branch cleanup), after it",
    "  applies `status:done` to the linked issue on a successful merge.",
    "",
    "Any other agent that manually applies `status:done` should call",
    "`unblock-dependents.sh <closed-issue>` as its last step. The",
    "sweep is idempotent, so double-invocation is a no-op.",
    "",
    "### What the sweep does",
    "",
    "The `unblock-dependents.sh` procedure, given a just-closed issue",
    "number, performs the following in one shell invocation:",
    "",
    "1. Searches open issues for `Depends on: #<closed>` references in",
    "   their body.",
    "2. Skips any match carrying `status:deferred`, emitting a",
    "   `SKIP_DEFERRED` line. A human-parked issue is never promoted by",
    "   a sweep, no matter how its dependencies resolve.",
    "3. For each remaining match, re-reads every dependency listed across",
    "   **all** `Depends on:` lines in the body (bodies routinely list",
    "   one dependency per line) and checks whether they are all closed.",
    "4. If **all** dependencies are closed, flips the dependent from",
    "   `status:blocked` to `status:ready` and posts a one-line comment",
    "   citing the resolving issue.",
    "5. If **some** dependencies are still open, leaves the dependent",
    "   as `status:blocked` and optionally posts a partial-unblock",
    "   comment (see the **Partial unblock behaviour** subsection",
    "   below).",
    "",
    "The sweep never flips an issue it cannot read cleanly. Dependents",
    "without a parseable `Depends on:` line are logged and skipped.",
    "",
    "### Search cap",
    "",
    "The `gh issue list --search` call that finds blocked dependents",
    "caps the result set at **100 issues**. The cap exists because",
    "`gh issue list` pages the search with `--limit`; raising the limit",
    "materialises the whole backlog into a single JSON payload, which",
    "gets expensive on large repos and bumps into GitHub search-API",
    "secondary rate limits. In practice 100 blocked dependents is far",
    "more than any healthy pipeline carries at once.",
    "",
    "When the backlog of `status:blocked` issues referencing the",
    "just-closed issue exceeds 100, the sweep quietly skips the",
    "overflow. Operators with > 100 blocked dependents should unblock",
    "the tail manually — either by running the sweep repeatedly (each",
    "run picks up 100 more) or by flipping `status:blocked` →",
    "`status:ready` directly on the outstanding issues.",
    "",
    "### Output format",
    "",
    "The script emits one line per processed dependent so agents can",
    "summarize the sweep's outcome in their session log:",
    "",
    "```",
    'UNBLOCKED #<dep> — all deps closed (<closed-list>) — "<title>"',
    'UNBLOCK_FAILED #<dep> — label flip failed (all deps closed: <closed-list>) — "<title>"',
    'STILL_BLOCKED #<dep> — waiting on <open-list> — "<title>"',
    'SKIP_DEFERRED #<dep> — status:deferred; human-parked, not promoting — "<title>"',
    "NO_DEPENDENTS #<closed>",
    "```",
    "",
    "`UNBLOCKED` lines mean the sweep transitioned the dependent to",
    "`status:ready`. `UNBLOCK_FAILED` lines mean every dependency was",
    "closed but the `gh issue edit` call rejected the label flip (API",
    "error, permissions, or the dependent went stale between the",
    "search and the edit) — a human needs to retry or flip the label",
    "manually. `STILL_BLOCKED` lines mean the dependent stays blocked",
    "pending other open deps. `SKIP_DEFERRED` lines mean the dependent",
    "carries `status:deferred` and was deliberately left blocked even",
    "though its dependencies resolved — the sweep never promotes a",
    "human-parked issue. `NO_DEPENDENTS` means no open issue",
    "references the just-closed issue in a `Depends on:` line.",
    "",
    "### Partial unblock behaviour",
    "",
  );

  if (ud.flagPartialUnblockWithAttention) {
    lines.push(
      "**Partial-unblock comments are enabled.** When a dependent has",
      "other open dependencies beyond the just-closed issue, the sweep",
      "posts a partial-unblock comment citing the remaining open deps.",
      "This gives operators a trail of incremental progress on",
      "multi-dependency chains without flooding the issue with one",
      "comment per resolved dep.",
    );
  } else {
    lines.push(
      "**Partial-unblock comments are disabled (default).** When a",
      "dependent has other open dependencies beyond the just-closed",
      "issue, the sweep logs `STILL_BLOCKED` to stdout but does not",
      "post a comment on the dependent. This keeps issue threads clean",
      "on multi-dependency chains — operators see the final unblock",
      "comment when the last dependency closes.",
      "",
      "Enable partial-unblock comments via",
      "`AgentConfigOptions.unblockDependents.flagPartialUnblockWithAttention = true`.",
    );
  }

  lines.push(
    "",
    "### Comment templates",
    "",
    "The sweep posts a comment on each fully-unblocked dependent.",
    "Consumers may override the comment body via",
    "`AgentConfigOptions.unblockDependents.commentTemplate`. The",
    "template substitutes `<CLOSED_ISSUE>` (the `#<n>` reference of",
    "the resolving issue) at comment-composition time.",
    "",
    "Default unblock comment:",
    "",
    "```markdown",
    ud.commentTemplate,
    "```",
  );

  if (ud.flagPartialUnblockWithAttention) {
    lines.push(
      "",
      "Default partial-unblock comment (`<OPEN_DEPS>` is a",
      "space-separated list of remaining open dependencies):",
      "",
      "```markdown",
      ud.partialUnblockCommentTemplate,
      "```",
    );
  }

  return lines.join("\n");
}

/**
 * Render the full `unblock-dependents.sh` script body for a given
 * resolved unblock-dependents config. The script takes a single
 * positional argument (the just-closed issue number) and emits one
 * line per processed dependent in the canonical
 * `UNBLOCKED` / `STILL_BLOCKED` / `NO_DEPENDENTS` format.
 *
 * Exported so `AgentConfig.preSynthesize` can emit the procedure
 * alongside `check-blocked.sh`.
 */
export function renderUnblockDependentsScript(
  ud: ResolvedUnblockDependents,
): string {
  const lines: Array<string> = [
    "#!/usr/bin/env bash",
    "# unblock-dependents.sh — Targeted dependency sweep after an issue closes.",
    "#",
    "# After any agent applies `status:done` to issue <N>, it calls",
    "# this script with that issue number. The script searches open",
    "# issues for `Depends on: #<N>` references in their body, checks",
    "# whether *all* listed dependencies on each match are now closed,",
    "# and flips fully-resolved dependents from `status:blocked` to",
    "# `status:ready` with a citation comment.",
    "#",
    "# The sweep is idempotent: calling it twice for the same closed",
    "# issue number is a no-op on the second pass because the first",
    "# pass already flipped the labels.",
    "#",
    "# Usage:",
    "#   .claude/procedures/unblock-dependents.sh <closed-issue-number>",
    "#",
    "# Exit codes:",
    "#   0 — sweep completed (with or without matches)",
    "#   1 — runtime error (e.g. gh command failed)",
    "#   2 — argument missing or malformed",
    "",
    "set -uo pipefail",
    "",
    "# ── constants ────────────────────────────────────────────────────────",
    "",
    `UNBLOCK_DEPENDENTS_ENABLED=${ud.enabled ? "1" : "0"}`,
    `UNBLOCK_DEPENDENTS_FLAG_PARTIAL=${ud.flagPartialUnblockWithAttention ? "1" : "0"}`,
    `UNBLOCK_COMMENT_TEMPLATE=${shellSingleQuote(ud.commentTemplate)}`,
    `UNBLOCK_PARTIAL_COMMENT_TEMPLATE=${shellSingleQuote(ud.partialUnblockCommentTemplate)}`,
    "",
    "# ── helpers ──────────────────────────────────────────────────────────",
    "",
    '# Extract issue numbers from a "Depends on:" text. The argument may be a',
    '# single line ("Depends on: #1, #2, #3") OR several Depends-on lines',
    "# joined into one string: bodies routinely list one dependency per line",
    "# (issue #881), so callers union every matching line before calling this.",
    "# Returns the space-separated union of every #<n> reference, or an empty",
    '# string when the text has no concrete references (e.g. a lone "(none)"',
    '# sentinel). References are extracted directly, so a stray "(none)"',
    "# concatenated with a real dep line never suppresses the real dependency",
    "# — only reference-free text counts as having no dependencies.",
    "parse_deps() {",
    '  local line="$1"',
    "  echo \"$line\" | grep -oE '#[0-9]+' | tr -d '#' | tr '\\n' ' ' || echo \"\"",
    "}",
    "",
    "# Check whether a single dependency is resolved. Returns 0 (resolved)",
    "# for a CLOSED issue or a MERGED PR, 1 otherwise. A Depends on: target",
    "# may reference a PR (state MERGED/OPEN/CLOSED): an OPEN PR keeps",
    "# blocking, MERGED resolves it, UNKNOWN (API error) stays conservative.",
    "is_closed() {",
    "  local state",
    '  state=$(gh issue view "$1" --json state --jq \'.state\' 2>/dev/null || echo "UNKNOWN")',
    '  [[ "$state" == "CLOSED" || "$state" == "MERGED" ]]',
    "}",
    "",
    "# ── main ─────────────────────────────────────────────────────────────",
    "",
    'closed_issue="${1:-}"',
    'if [[ -z "$closed_issue" ]]; then',
    '  echo "Usage: unblock-dependents.sh <closed-issue-number>" >&2',
    "  exit 2",
    "fi",
    "",
    "# Strip a leading '#' so callers can pass either `123` or `#123`.",
    'closed_issue="${closed_issue#\\#}"',
    "",
    'if ! [[ "$closed_issue" =~ ^[0-9]+$ ]]; then',
    '  echo "Usage: unblock-dependents.sh <closed-issue-number>" >&2',
    "  exit 2",
    "fi",
    "",
    'if [[ "$UNBLOCK_DEPENDENTS_ENABLED" != "1" ]]; then',
    '  echo "NO_DEPENDENTS #${closed_issue} (sweep disabled)"',
    "  exit 0",
    "fi",
    "",
    "# Find every open blocked issue whose body contains",
    "# `Depends on:` and mentions the just-closed issue. The gh search",
    "# is inclusive — we re-filter in the loop below against the exact",
    "# `#<closed>` token to avoid substring false-positives (e.g.",
    "# `#1234` matching a query for `#123`).",
    "#",
    "# The --limit 100 cap is intentional: raising it materialises the",
    "# whole backlog into a single JSON payload (expensive on large",
    "# repos) and bumps into GitHub search-API secondary rate limits.",
    "# Operators with >100 blocked dependents should unblock the tail",
    "# manually — re-running the sweep picks up the next 100, or flip",
    "# `status:blocked` → `status:ready` directly on the overflow.",
    'dependents=$(gh issue list --label "status:blocked" --state open \\',
    '  --search "in:body \\"Depends on:\\"" \\',
    '  --json number,title,body,labels --limit 100 2>/dev/null || echo "[]")',
    "",
    "count=$(echo \"$dependents\" | jq 'length')",
    'if [[ "$count" -eq 0 ]]; then',
    '  echo "NO_DEPENDENTS #${closed_issue}"',
    "  exit 0",
    "fi",
    "",
    "# Human-parked candidates (`status:deferred`). Collected jq-side into a",
    "# space-delimited lookup string rather than threaded through the",
    "# tab-separated records below: an extra TSV field would either sit after",
    "# dep_line (displacing the mandatory-last optional field) or before it",
    "# (reintroducing the #884 IFS tab-collapse bug). The skip itself happens",
    "# inside the loop, after the closed-issue reference test, so an unrelated",
    "# deferred issue in the search window never emits a line. No agent or",
    "# sweep ever removes `status:deferred` — only a human promotes it.",
    'deferred_set=" $(echo "$dependents" | jq -r \'',
    '  .[] | select(.labels | map(.name) | index("status:deferred")) | .number',
    "' | tr '\\n' ' ')\"",
    "",
    "# Union every `Depends on:` line per candidate (a body may list one",
    "# dependency per line) into a single space-joined dep string and stream",
    "# as tab-separated number / title / dep-line tuples.",
    'matches=$(echo "$dependents" | jq -r \'',
    "  .[] |",
    '  (.body | split("\\n") | map(select(test("Depends on:"; "i"))) | join(" ")) as $dep_line |',
    '  "\\(.number)\\t\\(.title)\\t\\($dep_line)"',
    "')",
    "",
    "found_any=false",
    "while IFS=$'\\t' read -r num title dep_line; do",
    '  [[ -z "$num" ]] && continue',
    '  [[ -z "$dep_line" ]] && continue',
    "",
    "  # Extract the dependency list and test whether the closed issue",
    "  # is actually referenced (exact `#<num>` match, not substring).",
    '  deps=$(parse_deps "$dep_line")',
    '  if [[ -z "${deps// /}" ]]; then',
    "    continue",
    "  fi",
    "",
    "  # Does this dependent actually reference the closed issue?",
    "  referenced=false",
    "  for dep in $deps; do",
    '    if [[ "$dep" == "$closed_issue" ]]; then',
    "      referenced=true",
    "      break",
    "    fi",
    "  done",
    "  if ! $referenced; then",
    "    continue",
    "  fi",
    "",
    "  found_any=true",
    "",
    "  # Deliberately-parked dependents never get promoted by a sweep, even",
    "  # when every dependency has closed. Only a human clears",
    "  # `status:deferred`.",
    '  if [[ "$deferred_set" == *" ${num} "* ]]; then',
    '    echo "SKIP_DEFERRED #${num} — status:deferred; human-parked, not promoting — \\"${title}\\""',
    "    continue",
    "  fi",
    "",
    "  # Re-check every dep in the list. Fully-closed → unblock.",
    "  all_closed=true",
    '  open_deps=""',
    '  closed_deps=""',
    "  for dep in $deps; do",
    '    if is_closed "$dep"; then',
    '      closed_deps="${closed_deps}#${dep} "',
    "    else",
    "      all_closed=false",
    '      open_deps="${open_deps}#${dep} "',
    "    fi",
    "  done",
    "",
    "  if $all_closed; then",
    "    comment=${UNBLOCK_COMMENT_TEMPLATE//<CLOSED_ISSUE>/#${closed_issue}}",
    "    # The edit is the label-flip that is the whole point of the sweep.",
    "    # If it fails we must report the failure — otherwise the agent log",
    "    # claims the dependent was unblocked when its status:blocked label",
    "    # is still on. The comment is best-effort (its absence is recoverable",
    "    # by re-running the sweep), so it stays soft-failed with `|| true`.",
    '    if gh issue edit "$num" \\',
    '        --remove-label "status:blocked" \\',
    '        --add-label "status:ready" >/dev/null 2>&1; then',
    '      gh issue comment "$num" --body "$comment" >/dev/null 2>&1 || true',
    '      echo "UNBLOCKED #${num} — all deps closed (${closed_deps% }) — \\"${title}\\""',
    "    else",
    '      echo "UNBLOCK_FAILED #${num} — label flip failed (all deps closed: ${closed_deps% }) — \\"${title}\\""',
    "    fi",
    "  else",
    '    if [[ "$UNBLOCK_DEPENDENTS_FLAG_PARTIAL" == "1" ]]; then',
    "      partial=${UNBLOCK_PARTIAL_COMMENT_TEMPLATE//<CLOSED_ISSUE>/#${closed_issue}}",
    "      partial=${partial//<OPEN_DEPS>/${open_deps% }}",
    '      gh issue comment "$num" --body "$partial" >/dev/null 2>&1 || true',
    "    fi",
    '    echo "STILL_BLOCKED #${num} — waiting on ${open_deps% } — \\"${title}\\""',
    "  fi",
    'done <<< "$matches"',
    "",
    "if ! $found_any; then",
    '  echo "NO_DEPENDENTS #${closed_issue}"',
    "fi",
  ];

  return lines.join("\n");
}

/*******************************************************************************
 *
 * Internal helpers
 *
 ******************************************************************************/

function assertNonEmpty(value: string, field: string): void {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(
      `UnblockDependentsConfig.${field} must be a non-empty string; got ${JSON.stringify(
        value,
      )}`,
    );
  }
}

/**
 * Escape a string for safe inclusion inside single quotes in a POSIX
 * shell script. Single quotes are closed, an escaped literal is
 * appended, and the single-quote context is reopened — the standard
 * POSIX-shell idiom.
 */
function shellSingleQuote(value: string): string {
  return `'${value.replace(/'/g, "'\\''")}'`;
}
